import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface EventView {
  event_id: string;
  display_name: string;
  current_phase: number;
  phase_name: string;
  boss_hp: number | null;
  boss_max_hp: number | null;
  community_progress: number;
  community_target: number | null;
  participant_count: number;
}

export default function Events({ session }: Props) {
  const [events, setEvents] = useState<EventView[]>([]);
  const [configured, setConfigured] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Damage/progress forms
  const [dmgPlayer, setDmgPlayer] = useState('player_1');
  const [dmgAmount, setDmgAmount] = useState('500');
  const [progPlayer, setProgPlayer] = useState('player_1');
  const [progAmount, setProgAmount] = useState('100');

  const base = `/api/v1/games/${session.gameId}/modules/event`;

  const reload = () => {
    fetch(`${base}/events`)
      .then(r => r.json())
      .then((d: { events: EventView[]; configured: string[] }) => {
        setEvents(d.events);
        setConfigured(d.configured);
      })
      .catch(e => setError(e.message));
  };

  useEffect(reload, [session.gameId]);

  const startEvent = async (eventId: string) => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/events/${eventId}/start`, { method: 'POST' });
      if (!res.ok) throw new Error(await res.text());
      reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const applyDamage = async (eventId: string) => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/events/${eventId}/damage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: dmgPlayer, damage: Number(dmgAmount) }),
      });
      if (!res.ok) throw new Error(await res.text());
      reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const addProgress = async (eventId: string) => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/events/${eventId}/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: progPlayer, amount: Number(progAmount) }),
      });
      if (!res.ok) throw new Error(await res.text());
      reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const completeEvent = async (eventId: string) => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/events/${eventId}/complete`, { method: 'POST' });
      if (!res.ok) throw new Error(await res.text());
      reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const activeIds = new Set(events.map(e => e.event_id));
  const inactiveConfigured = configured.filter(id => !activeIds.has(id));

  return (
    <Container>
      <PageHeader
        title="Events"
        subtitle="Dynamic events, boss encounters, and community goals."
      />
      {error && <div className="error-banner">{error}</div>}

      {/* Start event buttons for configured but not-yet-active events */}
      {inactiveConfigured.length > 0 && (
        <div className="card mb-5 p-4">
          <h3 className="text-sm font-semibold mb-3">Start Event</h3>
          <div className="flex gap-2 flex-wrap">
            {inactiveConfigured.map(id => (
              <button key={id} className="btn btn-primary" disabled={busy} onClick={() => startEvent(id)}>
                Start: {id}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Active events */}
      {events.length > 0 && (
        <div className="grid gap-4">
          {events.map(ev => {
            const hpPct = ev.boss_hp != null && ev.boss_max_hp
              ? Math.round((ev.boss_hp / ev.boss_max_hp) * 100)
              : null;
            const progPct = ev.community_target
              ? Math.min(100, Math.round((ev.community_progress / ev.community_target) * 100))
              : null;

            return (
              <div key={ev.event_id} className="card p-4">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{ev.display_name}</span>
                      <span className="badge badge-muted">{ev.phase_name}</span>
                    </div>
                    <div className="text-xs text-fg-muted">
                      Phase {ev.current_phase} | {ev.participant_count} participants
                    </div>
                  </div>
                  <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => completeEvent(ev.event_id)}>Complete</button>
                </div>

                {/* Boss HP bar */}
                {hpPct !== null && (
                  <div className="mb-3">
                    <div className="text-xs text-fg-muted mb-1">Boss HP: {ev.boss_hp}/{ev.boss_max_hp}</div>
                    <div className="flex-1 h-3 rounded-full bg-bg-2 overflow-hidden">
                      <div className="h-full rounded-full bg-red-500 transition-all" style={{ width: `${hpPct}%` }} />
                    </div>
                  </div>
                )}

                {/* Community progress bar */}
                {progPct !== null && (
                  <div className="mb-3">
                    <div className="text-xs text-fg-muted mb-1">Community: {ev.community_progress}/{ev.community_target}</div>
                    <div className="flex-1 h-3 rounded-full bg-bg-2 overflow-hidden">
                      <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${progPct}%` }} />
                    </div>
                  </div>
                )}

                {/* Action forms */}
                <div className="flex gap-3 items-end flex-wrap mt-2">
                  {ev.boss_hp != null && (
                    <>
                      <div>
                        <label className="text-xs text-fg-muted block mb-1">Player</label>
                        <input className="input w-28" value={dmgPlayer} onChange={e => setDmgPlayer(e.target.value)} />
                      </div>
                      <div>
                        <label className="text-xs text-fg-muted block mb-1">Damage</label>
                        <input className="input w-20" type="number" value={dmgAmount} onChange={e => setDmgAmount(e.target.value)} />
                      </div>
                      <button className="btn btn-sm" disabled={busy} onClick={() => applyDamage(ev.event_id)}>Hit Boss</button>
                    </>
                  )}
                  <div>
                    <label className="text-xs text-fg-muted block mb-1">Player</label>
                    <input className="input w-28" value={progPlayer} onChange={e => setProgPlayer(e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs text-fg-muted block mb-1">Progress</label>
                    <input className="input w-20" type="number" value={progAmount} onChange={e => setProgAmount(e.target.value)} />
                  </div>
                  <button className="btn btn-sm" disabled={busy} onClick={() => addProgress(ev.event_id)}>Add Progress</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {events.length === 0 && inactiveConfigured.length === 0 && (
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">No events configured</div>
          <div className="text-xs text-fg-muted">Create an event_definition in Config Explorer, publish, then manage here.</div>
        </div>
      )}
    </Container>
  );
}
